import type { EntityFilter } from 'App/pages/AdvancedSearch/AdvancedSearchEntities/EntityFilter';
import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';
import ProjectResource from 'App/resources/Project';
import type Project from 'App/resources/Model/Project';
import ProductResource from 'App/babel/resources/Product';
import ProductAttributes from 'App/babel/attributes/ProductAttributes';
import type Product from 'App/resources/Model/Product';
import { getRolesFromState, getUsersFromState } from 'Store/selectors/AppSelectors';
import type { AppState } from 'Store/reducers/AppReducer';
import t from 'Components/Helpers/translate';
import UserRoleTree from 'Components/Helpers/UserRoleTree';

export const getHasFieldSelectOptions = () => [
	{ value: 'all' as const, title: t('filters.all'), inactive: true },
	{ value: true, title: t('default.has') },
	{ value: false, title: t('default.hasNot') }
];

export type FilterNameTypes = 'ACCOUNT' | 'CONTACT' | 'ACTIVITY' | 'APPOINTMENT' | 'OPPORTUNITY' | 'ORDER' | 'ORDERROW';

export enum AdvancedSearchType {
	accounts = 'accounts',
	contacts = 'contacts',
	activities = 'activities',
	appointments = 'appointments',
	opportunities = 'opportunities',
	orders = 'orders'
}

export const filterNameMap = {
	[AdvancedSearchType.accounts]: 'ACCOUNT',
	[AdvancedSearchType.contacts]: 'CONTACT',
	[AdvancedSearchType.activities]: 'ACTIVITY',
	[AdvancedSearchType.appointments]: 'APPOINTMENT',
	[AdvancedSearchType.opportunities]: 'OPPORTUNITY',
	[AdvancedSearchType.orders]: 'ORDER'
} as const;

export const customFieldEntityMap = {
	[AdvancedSearchType.accounts]: 'account',
	[AdvancedSearchType.contacts]: 'contact',
	[AdvancedSearchType.activities]: 'activity',
	[AdvancedSearchType.appointments]: 'appointment',
	[AdvancedSearchType.opportunities]: 'opportunity',
	[AdvancedSearchType.orders]: 'order'
} as const;

export enum ListViewMap {
	accounts = 'advancedSearchAccount',
	contacts = 'advancedSearchContact',
	activities = 'advancedSearchActivity',
	appointments = 'advancedSearchAppointment',
	opportunities = 'advancedSearchOpportunity',
	orders = 'advancedSearchOrder'
}

export enum AdvancedSearchEntity {
	accounts = 'Client',
	contacts = 'Contact',
	activities = 'Activity',
	appointments = 'Appointment',
	opportunities = 'Opportunity',
	orders = 'Order'
}

export function getTypesObject<T>(value: T) {
	return (Object.keys(AdvancedSearchType) as AdvancedSearchType[]).reduce(
		(r, t) => ({ ...r, [t]: value }),
		{} as Record<AdvancedSearchType, T>
	);
}

const loggedInUserOption = () => ({ id: 'self' as string | number, title: t('default.loggedInUser') });

export const createUserSelectOptions = (accessType: string) => () => {
	const tree = UserRoleTree({
		accessType,
		roleIdPrefix: true,
		selectableRoles: true,
		formatUser: (u: any) => {
			u.title = u.name;
			return u;
		},
		formatRole: (r: any) => {
			r.title = r.name;
			return r;
		}
	});
	return [loggedInUserOption(), ...tree];
};

export const createUserFlatOptions = (accessType: string) => (App: AppState) => {
	const roleOptions = getRolesFromState(App, accessType).map(r => ({
		id: ('role-' + r.id) as string | number,
		title: r.name
	}));
	const userOptions = getUsersFromState(App, accessType).map(u => ({
		id: u.id as string | number,
		title: u.name
	}));
	return [loggedInUserOption(), ...roleOptions, ...userOptions];
};

export const createCampaignFetcher = () => (searchStr: string, ids?: number[]) => {
	const rb = new RequestBuilder();
	if (ids?.length) {
		rb.addFilter({ field: 'id' }, comparisonTypes.Equals, ids);
	} else if (searchStr) {
		rb.addFilter({ field: 'name' }, comparisonTypes.Search, searchStr);
	}
	rb.fields = ['id', 'name'];
	rb.addSort('name', true);
	rb.limit = 50;
	return ProjectResource.find(rb.build()).then(({ data }) =>
		data.map((c: Project) => ({ id: c.id.toString(), title: c.name }))
	);
};

export const createProductFetcher = () => (searchStr: string, ids?: number[]) => {
	const rb = new RequestBuilder();
	rb.addFilter(ProductAttributes.active, comparisonTypes.Equals, 1);
	if (ids?.length) {
		rb.addFilter(ProductAttributes.id, comparisonTypes.Equals, ids);
	} else if (searchStr) {
		rb.addFilter(ProductAttributes.name, comparisonTypes.Search, searchStr);
	}
	rb.fields = ['id', 'name'];
	rb.addSort('name', true);
	rb.limit = 50;
	return ProductResource.find(rb.build()).then(({ data }: { data: Product[] }) =>
		data.map(p => ({ id: p.id.toString(), title: p.name }))
	);
};

export const fixOrderFilters = (filters: EntityFilter): EntityFilter => {
	const result = { ...filters };
	TypedObject.keys(result).forEach(key => {
		const entityFilters = result[key];
		if (!entityFilters) {
			return;
		}

		let changed = false;
		const fixedEntityFilters = { ...entityFilters };

		(['OrderFilters', 'OpportunityFilters'] as const).forEach(filterKey => {
			const filter = fixedEntityFilters[filterKey];
			if (!filter?.value) return;
			const filterValue = filter.value;

			// Only fix if array contains objects
			if (filterValue?.OrderRowProduct?.value?.[0]?.id || filterValue?.OrderRowProductCategory?.value?.[0]?.id) {
				const productIds: number[] = [];
				const categoryIds: number[] = [];
				const selectedOrder: { id: number; isCategory?: boolean }[] = filterValue?.OrderRowProduct?.value || [];

				selectedOrder.forEach(product => {
					if (product.isCategory) {
						categoryIds.push(product.id);
					} else {
						productIds.push(product.id);
					}
				});

				const fixedValue = { ...filterValue };
				if (fixedValue.OrderRowProduct?.value) {
					fixedValue.OrderRowProduct = { ...fixedValue.OrderRowProduct, value: productIds };
				}
				if (fixedValue.OrderRowProductCategory?.value) {
					fixedValue.OrderRowProductCategory = {
						...fixedValue.OrderRowProductCategory,
						value: categoryIds,
						comparisonType: fixedValue.OrderRowProduct.comparisonType
					};
				}
				fixedEntityFilters[filterKey] = { ...filter, value: fixedValue };
				changed = true;
			}
		});

		if (changed) {
			result[key] = fixedEntityFilters;
		}
	});
	return result;
};
